// #region Imports
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import MuiDrawer from "@mui/material/Drawer";
import { shellBottomBarHeight } from "_shared/_views/shell-bottom-bar";
import IconButton from "@mui/material/IconButton";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { SIDE_PANEL_WIDE_WIDTH, SIDE_PANEL_WIDTH } from "_shared/_views/side-panel-width";
import Tooltip from "@mui/material/Tooltip";
import EntityMember from "_shared/entity-members/entity-member";
import Typography from "@mui/material/Typography";
import EditFeeScheduleCategory from "_shared/fee-schedules/categories/_views/edit-fee-schedule-category";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import FeeScheduleTeamRoster from "_shared/fee-schedules/categories/fee-schedule-team-roster";
import type { FeeScheduleCategoryEditorFocus } from "_shared/fee-schedules/categories/_views/fee-schedule-category-editor-focus";
import { useState } from "react";
import { FeeScheduleCategoryMaskedCurrency, MaskedFeeSchedule } from "work/masked-fields";
import FeeScheduleCategoryRedlineForm from "work/proposals/redlining/fee-schedule-redline/_views/fee-schedule-category-redline-form";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";
import VendorTeamMemberSelection from "work/work-agreements/teams/vendor-team/_views/members/vendor-team-member-selection";
import { shellTopBannersHeight } from "_shared/_views/shell-top-banners";
// #endregion

/** The app bar's height below `sm`, as `side-nav.tsx` has it. */
const PHONE_APP_BAR_HEIGHT = 56;

// #region Styled Components
const Drawer = styled(MuiDrawer)(({ theme }) => ({
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    // Ends above the assistant's bar below lg, which stays reachable while the panel is open (#6128).
    height: `calc(100% - 64px - ${shellTopBannersHeight()} - ${shellBottomBarHeight()})`,
    top: `calc(64px + ${shellTopBannersHeight()})`,
    width: SIDE_PANEL_WIDTH,
    // A phone's app bar is 56px, not 64px. The panel lies over the page there and is fixed to the
    // window, so it runs from that bar to the assistant's, and not to the page's footer (#7004).
    [theme.breakpoints.down("sm")]: {
      height: `calc(100% - ${PHONE_APP_BAR_HEIGHT}px - ${shellTopBannersHeight()} - ${shellBottomBarHeight()})`,
      top: `calc(${PHONE_APP_BAR_HEIGHT}px + ${shellTopBannersHeight()})`,
    },
  },
}));
// Twice as wide while the team member picker is up: at the panel's own width its marketplace result
// cards and filters were cramped (#5798). Only the paper widens, so it lies over the page meanwhile.
const FeeCategoryPanel = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "closeAfterTransition" && prop !== "wide",
})<{ open?: boolean; wide?: boolean }>(({ theme, open, wide }) => ({
  "& .MuiDrawer-paper": {
    width: open ? (wide ? SIDE_PANEL_WIDE_WIDTH : SIDE_PANEL_WIDTH) : 0,
    // A phone's whole width: the strip of page a side panel leaves there is too narrow to use, and the
    // panel's fields wrap less across all of it (#7004).
    [theme.breakpoints.down("sm")]: {
      width: open ? "100%" : 0,
    },
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: open
        ? theme.transitions.easing.easeOut
        : theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
  },
}));
const FeeCategoryPanelContainer = styled("div")(() => ({
  flex: 1,
  height: "100%",
  minHeight: 0,
}));
const FeeCategoryPanelContent = styled("div", {
  shouldForwardProp: (prop) => prop !== "concealed",
})<{ concealed: boolean }>(({ theme, concealed }) => ({
  display: concealed ? "none" : "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  padding: theme.spacing(2),
  // Fills the paper rather than restating its width, which would put the padding outside the panel.
  boxSizing: "border-box",
  width: "100%",
  [theme.breakpoints.down("sm")]: {
    paddingTop: theme.spacing(1),
  },
}));
const TitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  justifyContent: "space-between",
  flexDirection: "row",
  paddingBottom: theme.spacing(1),
  // On a phone the title and its large minimize button took 75px of a short panel, which the fields
  // need more (#7004).
  [theme.breakpoints.down("sm")]: {
    "& .MuiTypography-root": theme.typography.h6,
    "& .MuiIconButton-root": {
      padding: theme.spacing(0.5),
    },
    "& .MuiSvgIcon-root": {
      fontSize: theme.typography.pxToRem(24),
    },
  },
}));
// The picker runs to the panel's edges: at the panel's narrowest the marketplace's result cards have
// no width to give to a gutter.
const TeamMemberPane = styled("section")(() => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  width: "100%",
}));
const TeamMemberPaneTitleBar = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(1),
  padding: theme.spacing(2, 2, 1, 1),
}));
// The only scroller is inside the picker. The panel lets it have every pixel below the title and
// scrolls nothing itself, which is what a dialog around the same picker could not manage.
const TeamMemberPaneContent = styled("div")(() => ({
  display: "flex",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
}));
// #endregion

// #region Props/Types
type EditFeeScheduleCategoryDrawerProps = {
  category?: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency;
  formKey?: string;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onCategoryAdded?: (category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => void;
  onCategoryUpdated?: (
    originalName: FeeScheduleCategoryName | null,
    category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) => void;
  selectedCategories?: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency> | MaskedFeeSchedule;
  /** See `EditFeeScheduleCategory`. Given, the panel offers the Team Member picker in place of the form. */
  offersTeamMember?: boolean;
  /** See `EditFeeScheduleCategory`. A template passes true, because it is a rate card. */
  teamMemberOnEveryFee?: boolean;
  /**
   * The proposal's vendor team, offered before the full picker (#5798). Given, the picker is where
   * the rate finds somebody new for the team, and says so. A template has no team and passes none.
   */
  teamRoster?: FeeScheduleTeamRoster;
  fieldErrors?: Readonly<Record<string, string>>;
  /**
   * Asks for a field to take the focus: the Team Member or the Fee, and each new request is one more
   * ask (#6097). A page passes one when the reader pressed an issue about that part of the rate.
   */
  focus?: FeeScheduleCategoryEditorFocus;
  /**
   * The row's redline, where the category being edited is under negotiation (#5570). Given together
   * with `category`, the panel draws each field with its redline, as the fee schedule grid does, in
   * place of the plain form. A template and a marketplace team's rates have no redline and pass none.
   */
  redline?: FeeScheduleCategoryRedlineOptions;
};

export type FeeScheduleCategoryRedlineOptions = {
  categoryRedline: FeeScheduleCategoryRedline;
  negotiationId?: string;
  /** Nothing in the category may be changed right now. */
  readOnly?: boolean;
};

/** The question the picker is answering: who the rate is for, or undefined when it was backed out of. */
type PendingTeamMemberChoice = {
  settle: (member: EntityMember | undefined) => void;
};
// #endregion

/**
 * Creates or edits one fee schedule category, in the right-hand panel.
 *
 * Choosing who a rate is for happens in this same panel (#5393). The Team Member field's picker takes
 * the panel's place, and choosing somebody or going back puts the form back as it was left. It used to
 * open in a dialog over the page, which covered the category being written, drew the panel behind it
 * as well, and scrolled its own content around the marketplace's list, which scrolls by itself.
 *
 * The form stays mounted while the picker is up, hidden rather than removed, so the category, the
 * description and the rate typed so far are still there on the way back.
 *
 * The picker is the one a proposal's vendor team is built from. Its Coworkers tab is the firm's own
 * roster, and its marketplace, network and bookmarks tabs are where everybody else is. The person who
 * bills an hourly rate belongs to the vendor, and the author of a rate is not always the vendor, so a
 * client naming somebody at another firm needs those tabs.
 *
 * On a proposal the field offers the vendor team first, and reaches this picker through "Browse for
 * new member" (#5798). Whoever is chosen here is new to the team, and the page adds them to it when
 * the rate is saved, because `WorkAgreement.ValidateFeeSchedule` requires an hourly rate's person to
 * be on that team at hire time. So the picker marks the people already on it, and its button says
 * "Add To Team". A template has no team, opens this picker straight from the field, and adds nobody.
 */
export default function EditFeeScheduleCategoryDrawer(props: Readonly<EditFeeScheduleCategoryDrawerProps>) {
  const {
    category,
    formKey,
    isOpen,
    onClose,
    onCategoryAdded,
    onCategoryUpdated,
    selectedCategories,
    offersTeamMember,
    fieldErrors,
    teamMemberOnEveryFee,
    teamRoster,
    redline,
    focus,
  } = props;

  // #region State
  const [pendingTeamMemberChoice, setPendingTeamMemberChoice] = useState<PendingTeamMemberChoice>();
  const [teamMemberFiltersAreOpen, setTeamMemberFiltersAreOpen] = useState(false);
  // On a phone the panel lies over the page, fixed to the window, rather than in the page's column.
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));

  // A page closes this panel for its own reasons too — opening the existing-category panel, a save
  // elsewhere — and none of them come through here. A question left open would reopen the panel on
  // the picker, so closing it answers "nobody new", the same as going back. Adjusted during render
  // rather than in an effect, so the closed panel never renders a frame still holding the question.
  if (!isOpen && pendingTeamMemberChoice) {
    pendingTeamMemberChoice.settle(undefined);
    setPendingTeamMemberChoice(undefined);
    setTeamMemberFiltersAreOpen(false);
  }
  // #endregion

  // #region Handlers
  function browseForTeamMember(): Promise<EntityMember | undefined> {
    return new Promise<EntityMember | undefined>((resolve) => {
      let settled = false;
      setPendingTeamMemberChoice({
        // The picker reports a choice and then asks to be closed, so the first answer is the one that
        // counts: the close after a choice must not turn it into a dismissal.
        settle: (member) => {
          if (settled) return;
          settled = true;
          resolve(member);
        },
      });
    });
  }

  function answerTeamMemberChoice(member: EntityMember | undefined) {
    pendingTeamMemberChoice?.settle(member);
    setPendingTeamMemberChoice(undefined);
    setTeamMemberFiltersAreOpen(false);
  }
  // #endregion

  // #region Render
  const choosingTeamMember = pendingTeamMemberChoice !== undefined;

  return (
    <FeeCategoryPanel
      open={isOpen}
      wide={choosingTeamMember}
      anchor="right"
      variant="persistent"
      closeAfterTransition={false}
      // What a page's side-panel column reads to stop clipping the paper to the column's own width.
      // See `ProposalSidePanelColumn`.
      slotProps={{
        paper: {
          "data-open": isOpen ? "true" : undefined,
          "data-wide": choosingTeamMember ? "true" : undefined,
          // What tells a page's side-panel column to leave the paper where this panel puts it.
          "data-overlay": isPhone ? "true" : undefined,
        } as object,
      }}
    >
      <FeeCategoryPanelContainer>
        <FeeCategoryPanelContent concealed={choosingTeamMember}>
          <TitleBar>
            <Typography variant="h5">{category ? "Edit Category" : "Create New Category"}</Typography>
            <Tooltip title="Minimize panel">
              <IconButton size="medium" onClick={onClose}>
                <KeyboardTabIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </TitleBar>
          {category && redline ? (
            <FeeScheduleCategoryRedlineForm
              key={category.id.value}
              categoryRedline={redline.categoryRedline}
              negotiationId={redline.negotiationId}
              readOnly={redline.readOnly}
              selectedCategories={selectedCategories}
              onCancel={onClose}
              onCategoryUpdated={onCategoryUpdated}
              onBrowseForTeamMember={offersTeamMember ? browseForTeamMember : undefined}
              teamRoster={teamRoster}
              focus={focus}
            />
          ) : (
            <EditFeeScheduleCategory
              key={category?.id.value ?? formKey ?? "new"}
              category={category}
              onCancel={onClose}
              onCategoryAdded={onCategoryAdded}
              onCategoryUpdated={onCategoryUpdated}
              selectedCategories={selectedCategories}
              offersTeamMember={offersTeamMember}
              fieldErrors={fieldErrors}
              teamMemberOnEveryFee={teamMemberOnEveryFee}
              onBrowseForTeamMember={offersTeamMember ? browseForTeamMember : undefined}
              teamRoster={teamRoster}
              focus={focus}
            />
          )}
        </FeeCategoryPanelContent>
        {choosingTeamMember && (
          <TeamMemberPane aria-label={teamRoster ? "Add Team Member" : "Select Team Member"}>
            {/* The marketplace's filters open over the picker and bring their own way back. */}
            {!teamMemberFiltersAreOpen && (
              <TeamMemberPaneTitleBar>
                <Tooltip title="Back to the category">
                  <IconButton aria-label="Back to the category" onClick={() => answerTeamMemberChoice(undefined)}>
                    <ArrowBackIcon />
                  </IconButton>
                </Tooltip>
                <Typography variant="h5">
                  {teamRoster ? "Add Team Member" : "Select Team Member"}
                </Typography>
              </TeamMemberPaneTitleBar>
            )}
            <TeamMemberPaneContent>
              <VendorTeamMemberSelection
                selectLeader={false}
                selectMember
                selectedTeamMemberUserIds={teamRoster?.members.map((member) => member.userId) ?? []}
                // On a template, choosing who bills a rate adds nobody to anything, so it asks nothing
                // of their profile either: a team asks for credentials when the template is applied to
                // one. On a proposal it adds them to the vendor team, and the button says so — and the
                // vendor team takes nobody without credentials (#5800).
                choiceLabel={teamRoster ? undefined : "Select"}
                requiresCredentials={teamRoster !== undefined}
                // Nobody from the client's own firm bills one of its rates (#6091). A template has no
                // client, and leaves out nobody.
                excludedLegalEntityId={teamRoster?.excludedLegalEntityId}
                onFilterViewOpenChange={setTeamMemberFiltersAreOpen}
                onNewSelection={(individual) => answerTeamMemberChoice(individual.toEntityMember())}
                closeDialog={() => answerTeamMemberChoice(undefined)}
              />
            </TeamMemberPaneContent>
          </TeamMemberPane>
        )}
      </FeeCategoryPanelContainer>
    </FeeCategoryPanel>
  );
  // #endregion
}
